import { useState } from 'react';
import { App, Button, ColorPicker, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useFlyerCategories, useFlyerTemplates, useCreateFlyerCategory, useUpdateFlyerCategory, useDeleteFlyerCategory } from '@/hooks/useFlyers';
import { useLanguages } from '@/hooks/useLanguages';
import type { FlyerCategory } from '@/types/flyer';

export function ManageCategoriesModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { message } = App.useApp();
  const catsQ = useFlyerCategories();
  const tplsQ = useFlyerTemplates();
  const langsQ = useLanguages();
  const create = useCreateFlyerCategory();
  const update = useUpdateFlyerCategory();
  const del = useDeleteFlyerCategory();
  const [lang, setLang] = useState('en');

  const langOptions = (langsQ.data ?? []).map((l) => ({ value: l.code, label: `${l.code} — ${l.name_native}` }));
  const countOf = (catId: string) => (tplsQ.data ?? []).filter((t) => t.category_id === catId).length;
  const err = (e: unknown) => message.error((e as Error).message);

  const setName = (c: FlyerCategory, v: string) =>
    update.mutate({ id: c.id, b: { name_json: { ...c.name_json, [lang]: v } } }, { onError: err });

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={640} title="Manage flyer categories">
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Select size="small" style={{ width: 220 }} value={lang} options={langOptions} onChange={setLang} />
        <Button size="small" icon={<PlusOutlined />} loading={create.isPending}
          onClick={() => create.mutate({ name_json: { en: 'New category' }, sort_order: (catsQ.data?.length ?? 0) }, { onError: err })}>
          Add category
        </Button>
      </Space>
      <Table size="small" rowKey="id" pagination={false} loading={catsQ.isLoading}
        dataSource={catsQ.data ?? []}
        columns={[
          {
            title: `Name (${lang})`, key: 'name',
            render: (_, c: FlyerCategory) => (
              <Input key={`${c.id}:${lang}`} size="small" defaultValue={c.name_json[lang] ?? ''} placeholder={c.name_json.en}
                onBlur={(e) => { if (e.target.value !== (c.name_json[lang] ?? '')) setName(c, e.target.value); }} />
            ),
          },
          {
            title: 'Color', key: 'color', width: 70,
            render: (_, c: FlyerCategory) => (
              <ColorPicker size="small" value={c.color ?? '#1677ff'}
                onChangeComplete={(v) => update.mutate({ id: c.id, b: { color: v.toHexString() } }, { onError: err })} />
            ),
          },
          {
            title: 'Sort', key: 'sort', width: 90,
            render: (_, c: FlyerCategory) => (
              <InputNumber size="small" style={{ width: 70 }} value={c.sort_order}
                onChange={(v) => { if (v !== null && v !== c.sort_order) update.mutate({ id: c.id, b: { sort_order: v } }, { onError: err }); }} />
            ),
          },
          {
            title: 'Templates', key: 'count', width: 90,
            render: (_, c: FlyerCategory) => <Typography.Text type="secondary">{countOf(c.id)}</Typography.Text>,
          },
          {
            title: '', key: 'del', width: 50,
            render: (_, c: FlyerCategory) => {
              const n = countOf(c.id);
              return (
                <Tooltip title={n > 0 ? `Blocked: ${n} template(s) still use this category (deleting would cascade-delete them)` : ''}>
                  <Popconfirm title="Delete this category permanently?" disabled={n > 0}
                    onConfirm={() => del.mutate(c.id, { onError: err })}>
                    <Button size="small" danger icon={<DeleteOutlined />} disabled={n > 0} />
                  </Popconfirm>
                </Tooltip>
              );
            },
          },
        ]} />
    </Modal>
  );
}
